import { useState } from "react";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import { ShieldCheck, Mail } from "lucide-react";

const REQUEST_TYPES = [
  {
    key: "access",
    label: "Access — see what data you hold on me",
    blurb: "We will compile a copy of every record we hold about you (contact submissions, attached photos, false-advertising reports) and an administrator will email it to you.",
  },
  {
    key: "erasure",
    label: "Erasure — delete all my data",
    blurb: "We will permanently delete every record and image we hold about you. This cannot be undone.",
  },
  {
    key: "rectification",
    label: "Rectification — correct my data",
    blurb: "Tell us what should be changed and what it currently says. An administrator will review and update it.",
  },
  {
    key: "restriction",
    label: "Restriction — stop processing my data",
    blurb: "We will mark your data so we no longer process it (e.g. don't publish a submission you made). It will not be deleted.",
  },
];

/**
 * Public GDPR data-subject request form. Used both inside the /contact 5th
 * tile and embedded inline on the /privacy page.
 *
 * Props:
 *   onSuccess?: () => void   — called after a successful submit
 *   compact?: boolean        — render a tighter layout (used on /privacy)
 */
export function GdprRequestForm({ onSuccess, compact = false }) {
  const [form, setForm] = useState({
    email: "",
    request_type: "access",
    details: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const selected = REQUEST_TYPES.find((r) => r.key === form.request_type);
  const detailsRequired = form.request_type === "rectification";

  async function onSubmit(e) {
    e.preventDefault();
    if (detailsRequired && !form.details.trim()) {
      toast.error("For a Rectification request, please describe what should be changed.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/public/gdpr-request", {
        email: form.email,
        request_type: form.request_type,
        details: form.details || null,
      });
      setSubmitted(true);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send request. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  if (submitted) {
    return (
      <div className="border border-line bg-ivory p-6" data-testid="gdpr-request-success">
        <div className="flex items-center gap-2 mb-2">
          <Mail className="w-5 h-5 text-forest" />
          <h3 className="font-serif text-xl">Request received.</h3>
        </div>
        <p className="text-sm text-muted_text leading-relaxed">
          We have logged your request. <strong>Before we process it</strong>, an
          administrator will send a confirmation link to{" "}
          <strong>{form.email}</strong> so you can prove ownership of this email
          address. Please look for it in your inbox over the next few days.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid="gdpr-request-form">
      {!compact && (
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck className="w-5 h-5 text-forest" />
          <h3 className="font-serif text-xl">Data privacy request</h3>
        </div>
      )}

      <div className="border-l-4 border-clay bg-clay/5 px-4 py-3 text-sm text-ink" data-testid="gdpr-request-warning">
        <strong className="block mb-1">Confirmation required.</strong>
        Your request will only be processed <em>after</em> you confirm ownership of the
        email address you provide. An administrator will send a confirmation link to
        that email — please check your inbox (and spam folder) over the next few days.
      </div>

      <label className="block">
        <span className="block text-xs uppercase tracking-wider text-muted_text mb-1.5">
          Email <span className="text-clay">*</span>
        </span>
        <input
          required
          type="email"
          value={form.email}
          onChange={set("email")}
          placeholder="you@example.com"
          className="w-full px-3.5 py-2.5 border border-line bg-white text-sm focus:outline-none focus:border-forest"
          data-testid="gdpr-request-email"
        />
      </label>

      <fieldset>
        <legend className="block text-xs uppercase tracking-wider text-muted_text mb-2">Request type <span className="text-clay">*</span></legend>
        <div className="space-y-2">
          {REQUEST_TYPES.map((r) => (
            <label key={r.key} className={`flex items-start gap-2 cursor-pointer p-2 border ${form.request_type === r.key ? "border-forest bg-forest/5" : "border-line"} hover:bg-ivory`} data-testid={`gdpr-request-type-${r.key}`}>
              <input
                type="radio"
                name="request_type"
                value={r.key}
                checked={form.request_type === r.key}
                onChange={set("request_type")}
                className="mt-0.5"
              />
              <span className="text-sm">
                <span className="font-medium block">{r.label}</span>
                <span className="text-xs text-muted_text leading-snug">{r.blurb}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <label className="block">
        <span className="block text-xs uppercase tracking-wider text-muted_text mb-1.5">
          Details {detailsRequired && <span className="text-clay">*</span>}
        </span>
        <textarea
          rows={compact ? 3 : 4}
          required={detailsRequired}
          value={form.details}
          onChange={set("details")}
          placeholder={
            detailsRequired
              ? "What should be corrected, and what does it currently say?"
              : "Optional — anything else we should know."
          }
          className="w-full px-3.5 py-2.5 border border-line bg-white text-sm focus:outline-none focus:border-forest"
          data-testid="gdpr-request-details"
        />
        <span className="block text-xs text-muted_text mt-1">Selected: <em>{selected?.label}</em></span>
      </label>

      <button
        type="submit"
        disabled={loading}
        className="w-full bg-forest text-ivory px-7 py-3 text-sm hover:bg-forest/90 disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid="gdpr-request-submit"
      >
        {loading ? "Sending…" : "Submit request"}
      </button>
    </form>
  );
}
